import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Sale, CartItem, StockValidationResult, Customer, Item, PaymentMethod, PaymentType } from '../types';
import { apiClient as api } from '../services/api';
import { useLanguage } from './LanguageContext';
import { rateLimitErrorMessage } from '../utils/errorMessage';
import { calculateCartTotals, calculateStoredPriceBreakdown, SalePriceBreakdown, toSaleItemRequest } from '../utils/salesPricing';
import { getInventoryMode, tracksBatches } from '../utils/inventoryMode';
import { initialState, shoppingCartReducer, ShoppingCartState } from './shoppingCartReducer';

interface ShoppingCartContextType {
  state: ShoppingCartState;
  selectCustomer: (customer: Customer | null) => void;
  addItem: (item: Item, quantity: number, batchId?: string, batchNumber?: string, pricing?: SalePriceBreakdown) => Promise<void>;
  updateItemQuantity: (itemId: string, quantity: number, batchId?: string, unitPrice?: number) => void;
  removeItem: (itemId: string, batchId?: string, unitPrice?: number) => void;
  clearCart: () => void;
  resetSale: () => void;
  validateStock: () => Promise<void>;
  getCartTotal: () => number;
  getCartItemCount: () => number;
  completeSale: (paymentType: PaymentType, paymentAmount?: number, paymentMethod?: PaymentMethod, notes?: string,
    paymentPlan?: { installmentCount: number; firstDueDate: string }) => Promise<Sale>;
}

const ShoppingCartContext = createContext<ShoppingCartContextType | undefined>(undefined);

export function ShoppingCartProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(shoppingCartReducer, initialState);
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const completeSaleMutation = useMutation({
    mutationFn: ({
      customerId,
      items,
      paymentType,
      paymentAmount,
      paymentMethod,
      paymentNotes,
      paymentPlan,
    }: {
      customerId: string;
      items: Array<{ itemId: string; quantity: number; unitPrice: number; batchId?: string }>;
      paymentType: PaymentType;
      paymentAmount?: number;
      paymentMethod?: PaymentMethod;
      paymentNotes?: string;
      paymentPlan?: { installmentCount: number; firstDueDate: string };
    }) => api.createAndCompleteSale({
      customerId,
      items,
      paymentType,
      paymentAmount,
      paymentMethod,
      paymentNotes,
      paymentPlan,
    }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['sales'] }),
        queryClient.invalidateQueries({ queryKey: ['debts'] }),
        queryClient.invalidateQueries({ queryKey: ['items'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
        queryClient.invalidateQueries({ queryKey: ['admin'] }),
      ]);
    },
  });

  // Note: Auto-save removed with simplified sales flow - items stored locally only

  const selectCustomer = (customer: Customer | null) => {
    dispatch({ type: 'SET_SELECTED_CUSTOMER', payload: customer });
  };

  const addItem = async (item: Item, quantity: number, batchId?: string, batchNumber?: string, pricing?: SalePriceBreakdown) => {
    if (!state.selectedCustomer) {
      dispatch({ type: 'SET_ERROR', payload: t('sales.selectCustomerBeforeAdding') });
      return;
    }

    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      // Skip stock validation for now to test cart functionality
      // const stockValidation = await api.validateItemStock(item.id, quantity, batchId);
      // if (!stockValidation.isValid) {
      //   dispatch({ type: 'SET_ERROR', payload: stockValidation.message || t('Stock validation failed') });
      //   return;
      // }

      // Always add to local cart first for immediate UI feedback
      const fallbackNet = item.price ?? item.priceGross ?? 0;
      const fallbackGross = item.priceGross ?? item.price ?? 0;
      const breakdown = pricing ?? calculateStoredPriceBreakdown(
        fallbackNet,
        fallbackGross,
        item.vatRate,
        quantity,
        item.priceEntryMode,
      );
      const cartItem: CartItem = {
        itemId: item.id,
        itemName: item.name,
        quantity,
        unitPrice: breakdown.unitGrossPrice,
        enteredUnitPrice: breakdown.enteredUnitPrice,
        unitNetPrice: breakdown.unitNetPrice,
        vatRate: breakdown.vatRate,
        netAmount: breakdown.netAmount,
        vatAmount: breakdown.vatAmount,
        totalPrice: breakdown.grossAmount,
        batchId,
        batchNumber,
        availableStock: item.totalStockQuantity || 0,
        enableBatchTracking: tracksBatches(item),
        itemType: item.itemType,
        inventoryMode: getInventoryMode(item),
      };

      dispatch({ type: 'ADD_ITEM', payload: cartItem });

      // Note: With simplified sales flow, we only store items locally
      // They will be sent to backend when completing the sale
    } catch (error: unknown) {
      dispatch({ type: 'SET_ERROR', payload: rateLimitErrorMessage(error, t('sales.failedToAddItem'), t) });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const updateItemQuantity = (itemId: string, quantity: number, batchId?: string, unitPrice?: number) => {
    if (quantity === 0) {
      // Remove item if quantity is 0
      removeItem(itemId, batchId, unitPrice);
    } else {
      // Update local cart only (simplified flow)
      dispatch({ type: 'UPDATE_ITEM_QUANTITY', payload: { itemId, quantity, batchId, unitPrice } });
    }
  };

  const removeItem = (itemId: string, batchId?: string, unitPrice?: number) => {
    // Remove from local cart only (simplified flow)
    dispatch({ type: 'REMOVE_ITEM', payload: { itemId, batchId, unitPrice } });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  const resetSale = () => {
    dispatch({ type: 'RESET_SALE' });
  };

  // Note: Draft sale functions removed in favor of simplified sales flow

  const validateStock = useCallback(async () => {
    if (state.cartItems.length === 0) return;

    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const validationResults: { [itemId: string]: StockValidationResult } = {};

      for (const cartItem of state.cartItems) {
        const result = await api.validateItemStock(
          cartItem.itemId, 
          cartItem.quantity, 
          cartItem.batchId
        );
        validationResults[cartItem.itemId] = result;
      }

      dispatch({ type: 'SET_STOCK_VALIDATION', payload: validationResults });
    } catch (error: unknown) {
      dispatch({ type: 'SET_ERROR', payload: rateLimitErrorMessage(error, t('sales.failedToValidateStock'), t) });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [state.cartItems, t]);

  const getCartTotal = (): number => {
    return calculateCartTotals(state.cartItems).grossAmount;
  };

  const getCartItemCount = (): number => {
    return state.cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  const completeSale = async (paymentType: PaymentType, paymentAmount?: number, paymentMethod?: PaymentMethod, notes?: string,
    paymentPlan?: { installmentCount: number; firstDueDate: string }): Promise<Sale> => {
    if (!state.selectedCustomer || state.cartItems.length === 0) {
      throw new Error(t('Customer and items are required'));
    }

    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      const completedSale = await completeSaleMutation.mutateAsync({
        customerId: state.selectedCustomer.id,
        items: state.cartItems.map(toSaleItemRequest),
        paymentType,
        paymentAmount,
        paymentMethod,
          paymentNotes: notes,
          paymentPlan,
        });

      // Clear the cart after successful completion
      dispatch({ type: 'CLEAR_CART' });

      return completedSale;
    } catch (error: unknown) {
      dispatch({ type: 'SET_ERROR', payload: rateLimitErrorMessage(error, t('sales.failedToCompleteSale'), t) });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const contextValue: ShoppingCartContextType = {
    state,
    selectCustomer,
    addItem,
    updateItemQuantity,
    removeItem,
    clearCart,
    resetSale,
    validateStock,
    getCartTotal,
    getCartItemCount,
    completeSale,
  };

  return (
    <ShoppingCartContext.Provider value={contextValue}>
      {children}
    </ShoppingCartContext.Provider>
  );
}

export function useShoppingCart() {
  const context = useContext(ShoppingCartContext);
  if (context === undefined) {
    throw new Error('useShoppingCart must be used within a ShoppingCartProvider');
  }
  return context;
}
